import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Heart, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Helpers — accept invite landing page.
 *
 * The owner emails/texts a /helpers/accept/<token> link. We:
 *   1. Require the helper to be signed in (redirect to /login with `next`).
 *   2. POST /helpers/redeem/<token> to accept. Backend logs consent + audit.
 *   3. Redirect to /privacy/me on success so they see what they now help with.
 *
 * Refusing a redeem is just closing the tab — there's no "decline" verb yet.
 * If the original owner revokes before acceptance, the API returns 410 and
 * we show a calm explanation here.
 */
export default function HelperAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    if (!user) {
      nav(`/login?next=/helpers/accept/${token}`, { replace: true });
    }
  }, [user, token, nav]);

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/helpers/redeem/${token}`);
      setAccepted(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 dotted-bg" data-testid="helper-accept-page">
      <div className="brutal-card bg-white p-6 max-w-md w-full">
        <div className="flex items-center gap-2 mb-3">
          <Heart size={18} className="text-[color:var(--coral)] fill-[color:var(--coral)]" />
          <h1 className="display text-2xl font-extrabold">You've been invited to help</h1>
        </div>
        {accepted ? (
          <>
            <div className="brutal-card bg-[#D6F5C8] p-3 mb-3 flex items-center gap-2" data-testid="helper-accept-success">
              <ShieldCheck size={18} className="text-[#3A8B3F]" />
              <div>
                <div className="display font-bold">Access granted</div>
                <div className="text-xs text-[color:var(--ink-soft)]">
                  You can {accepted.permissions.join(", ")} on this board.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/privacy/me" className="pill-btn primary flex-1 justify-center" data-testid="helper-accept-go">
                Open my privacy dashboard
              </Link>
            </div>
          </>
        ) : error ? (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-3 flex items-start gap-2" data-testid="helper-accept-error">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <div className="display font-bold">We couldn't accept this invite</div>
              <div className="text-sm">{error}</div>
              <div className="text-xs mt-2 text-[color:var(--ink-soft)]">
                Ask the owner of the board to send you a fresh invite.
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[color:var(--ink-soft)] mb-3 leading-relaxed">
              Accepting this invite lets you support someone else's
              communication board with the permissions they chose for you.
              You can stop helping at any time, and the owner can revoke
              your access at any time.
            </p>
            <p className="text-xs text-[color:var(--ink-soft)] mb-4 italic">
              Signed in as <strong>{user.email}</strong>. We'll log this acceptance with a timestamp.
            </p>
            <button
              type="button"
              className="pill-btn primary w-full justify-center"
              onClick={accept}
              disabled={busy}
              data-testid="helper-accept-submit"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> Accepting…</> : "Accept invitation"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
